<template>
  <div class="data-table-wrapper" :class="{ 'is-loading': loading }">
    <!-- Table Header with Search & Filters -->
    <div class="data-table-header" v-if="showHeader">
      <div class="data-table-search" v-if="searchable">
        <div class="search-input-wrapper">
          <vue-feather type="search" size="16" class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
            @input="handleSearch"
          />
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <vue-feather type="x" size="14" />
          </button>
        </div>
      </div>
      <div class="data-table-actions">
        <slot name="filters"></slot>
        <slot name="actions"></slot>
      </div>
    </div>

    <!-- Results Count -->
    <div class="data-table-info" v-if="showInfo && !loading">
      <span class="results-count">
        {{ totalItems }} {{ totalItems === 1 ? 'result' : 'results' }}
        <span v-if="searchQuery" class="search-term">for "{{ searchQuery }}"</span>
      </span>
      <div class="view-options" v-if="showViewOptions">
        <button 
          class="view-btn" 
          :class="{ active: viewMode === 'table' }"
          @click="viewMode = 'table'"
          title="Table view"
        >
          <vue-feather type="list" size="16" />
        </button>
        <button 
          class="view-btn" 
          :class="{ active: viewMode === 'grid' }"
          @click="viewMode = 'grid'"
          title="Grid view"
        >
          <vue-feather type="grid" size="16" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="data-table-loading">
      <div class="loading-spinner"></div>
      <span>Loading...</span>
    </div>

    <!-- Table Content -->
    <div v-else-if="items.length" class="data-table-content">
      <div class="table-scroll-container">
        <table class="data-table" :class="tableClass">
          <thead>
            <tr>
              <th v-if="selectable" class="col-checkbox">
                <label class="checkbox-wrapper">
                  <input 
                    type="checkbox" 
                    :checked="allSelected"
                    ref="selectAllCheckbox"
                    @change="toggleSelectAll"
                  />
                  <span class="checkbox-custom"></span>
                </label>
              </th>
              <th 
                v-for="column in visibleColumns" 
                :key="column.key"
                :class="[
                  column.class,
                  { 'sortable': column.sortable, 'sorted': sortKey === column.key }
                ]"
                :style="{ width: column.width, minWidth: column.minWidth }"
                @click="column.sortable && handleSort(column.key)"
              >
                <div class="th-content">
                  <span>{{ column.label }}</span>
                  <span v-if="column.sortable" class="sort-icon">
                    <vue-feather 
                      v-if="sortKey === column.key"
                      :type="sortDir === 'asc' ? 'chevron-up' : 'chevron-down'" 
                      size="14"
                    />
                    <vue-feather 
                      v-else
                      type="code" 
                      size="14"
                      style="transform: rotate(90deg); opacity: 0.3;"
                    />
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(item, index) in paginatedItems" 
              :key="getItemKey(item, index)"
              :class="{ 'selected': isSelected(item), 'hoverable': hoverable }"
              @click="handleRowClick(item)"
            >
              <td v-if="selectable" class="col-checkbox">
                <label class="checkbox-wrapper" @click.stop>
                  <input 
                    type="checkbox" 
                    :checked="isSelected(item)"
                    @change="toggleSelect(item)"
                  />
                  <span class="checkbox-custom"></span>
                </label>
              </td>
              <td 
                v-for="column in visibleColumns" 
                :key="column.key"
                :class="column.cellClass"
                :data-label="column.label"
              >
                <slot :name="`cell-${column.key}`" :item="item" :value="getNestedValue(item, column.key)">
                  {{ formatValue(getNestedValue(item, column.key), column) }}
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="data-table-empty">
      <slot name="empty">
        <div class="empty-state">
          <div class="empty-icon">
            <vue-feather :type="emptyIcon" size="48" />
          </div>
          <h3 class="empty-title">{{ emptyTitle }}</h3>
          <p class="empty-description">{{ emptyDescription }}</p>
          <slot name="empty-action"></slot>
        </div>
      </slot>
    </div>

    <!-- Pagination -->
    <div v-if="paginated && items.length && totalPages > 1" class="data-table-pagination">
      <div class="pagination-info">
        Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalItems }}
      </div>
      <div class="pagination-controls">
        <button 
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="goToPage(1)"
          title="First page"
        >
          <vue-feather type="chevrons-left" size="16" />
        </button>
        <button 
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
          title="Previous page"
        >
          <vue-feather type="chevron-left" size="16" />
        </button>
        
        <div class="pagination-pages">
          <button
            v-for="page in visiblePages"
            :key="page"
            class="pagination-page"
            :class="{ active: page === currentPage, ellipsis: page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && goToPage(page)"
          >
            {{ page }}
          </button>
        </div>

        <button 
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
          title="Next page"
        >
          <vue-feather type="chevron-right" size="16" />
        </button>
        <button 
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="goToPage(totalPages)"
          title="Last page"
        >
          <vue-feather type="chevrons-right" size="16" />
        </button>
      </div>
      <div class="pagination-size">
        <select v-model="internalPageSize" class="page-size-select" @change="handlePageSizeChange">
          <option v-for="size in pageSizeOptions" :key="size" :value="size">
            {{ size }} per page
          </option>
        </select>
      </div>
    </div>

    <!-- Selected Actions Bar -->
    <transition name="slide-up">
      <div v-if="selectedItems.length" class="selection-bar">
        <span class="selection-count">{{ selectedItems.length }} selected</span>
        <slot name="bulk-actions" :selected="selectedItems">
          <button class="btn btn-sm btn-outline-danger" @click="$emit('bulk-delete', selectedItems)">
            <vue-feather type="trash-2" size="14" class="me-1" />
            Delete Selected
          </button>
        </slot>
        <button class="selection-clear" @click="clearSelection">
          <vue-feather type="x" size="14" />
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'

export default {
  name: 'DataTable',
  props: {
    items: { type: Array, default: () => [] },
    columns: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search...' },
    selectable: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: true },
    paginated: { type: Boolean, default: true },
    pageSize: { type: Number, default: 10 },
    pageSizeOptions: { type: Array, default: () => [10, 25, 50, 100] },
    itemKey: { type: String, default: 'id' },
    showHeader: { type: Boolean, default: true },
    showInfo: { type: Boolean, default: true },
    showViewOptions: { type: Boolean, default: false },
    emptyIcon: { type: String, default: 'inbox' },
    emptyTitle: { type: String, default: 'No data found' },
    emptyDescription: { type: String, default: 'Try adjusting your search or filters' },
    tableClass: { type: String, default: '' },
    defaultSort: { type: String, default: '' },
    defaultSortDir: { type: String, default: 'asc' }
  },
  emits: ['search', 'sort', 'page-change', 'row-click', 'selection-change', 'bulk-delete'],
  setup(props, { emit }) {
    const searchQuery = ref('')
    const sortKey = ref(props.defaultSort)
    const sortDir = ref(props.defaultSortDir)
    const currentPage = ref(1)
    const internalPageSize = ref(props.pageSize)
    const selectedItems = ref([])
    const viewMode = ref('table')
    const selectAllCheckbox = ref(null)

    const visibleColumns = computed(() => 
      props.columns.filter(col => col.visible !== false)
    )

    const filteredItems = computed(() => {
      let result = Array.isArray(props.items) ? [...props.items] : []
      
      // Client-side search
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(item => 
          props.columns.some(col => {
            if (col.searchable === false) return false
            const value = getNestedValue(item, col.key)
            return value && String(value).toLowerCase().includes(query)
          })
        )
      }
      
      // Client-side sort
      if (sortKey.value) {
        result.sort((a, b) => {
          const aVal = getNestedValue(a, sortKey.value)
          const bVal = getNestedValue(b, sortKey.value)
          
          if (aVal === bVal) return 0
          if (aVal === null || aVal === undefined) return 1
          if (bVal === null || bVal === undefined) return -1
          
          const comparison = aVal < bVal ? -1 : 1
          return sortDir.value === 'asc' ? comparison : -comparison
        })
      }
      
      return result
    })

    const totalItems = computed(() => filteredItems.value.length)
    const totalPages = computed(() => Math.ceil(totalItems.value / internalPageSize.value))
    const startIndex = computed(() => (currentPage.value - 1) * internalPageSize.value)
    const endIndex = computed(() => Math.min(startIndex.value + internalPageSize.value, totalItems.value))

    const paginatedItems = computed(() => {
      if (!props.paginated) return filteredItems.value
      return filteredItems.value.slice(startIndex.value, endIndex.value)
    })

    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        
        for (let i = start; i <= end; i++) pages.push(i)
        
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      
      return pages
    })

    const allSelected = computed(() => 
      paginatedItems.value.length > 0 && 
      paginatedItems.value.every(item => isSelected(item))
    )

    const someSelected = computed(() => 
      selectedItems.value.length > 0 && !allSelected.value
    )

    function getNestedValue(obj, path) {
      if (!path) return obj
      return path.split('.').reduce((acc, part) => acc?.[part], obj)
    }

    function getItemKey(item, index) {
      return item[props.itemKey] ?? index
    }

    function formatValue(value, column) {
      if (value === null || value === undefined) return '-'
      if (column.formatter) return column.formatter(value)
      return value
    }

    function handleSearch() {
      currentPage.value = 1
      emit('search', searchQuery.value)
    }

    function clearSearch() {
      searchQuery.value = ''
      handleSearch()
    }

    function handleSort(key) {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortKey.value = key
        sortDir.value = 'asc'
      }
      emit('sort', { key: sortKey.value, dir: sortDir.value })
    }

    function goToPage(page) {
      currentPage.value = page
      emit('page-change', { page, pageSize: internalPageSize.value })
    }

    function handlePageSizeChange() {
      currentPage.value = 1
      emit('page-change', { page: 1, pageSize: internalPageSize.value })
    }

    function handleRowClick(item) {
      emit('row-click', item)
    }

    function isSelected(item) {
      return selectedItems.value.some(s => getItemKey(s) === getItemKey(item))
    }

    function toggleSelect(item) {
      if (isSelected(item)) {
        selectedItems.value = selectedItems.value.filter(s => getItemKey(s) !== getItemKey(item))
      } else {
        selectedItems.value.push(item)
      }
      emit('selection-change', selectedItems.value)
    }

    function toggleSelectAll() {
      if (allSelected.value) {
        selectedItems.value = []
      } else {
        selectedItems.value = [...paginatedItems.value]
      }
      emit('selection-change', selectedItems.value)
    }

    function clearSelection() {
      selectedItems.value = []
      emit('selection-change', selectedItems.value)
    }

    // Reset page when items change
    watch(() => props.items, () => {
      if (currentPage.value > totalPages.value) {
        currentPage.value = Math.max(1, totalPages.value)
      }
    })

    // Handle indeterminate state for select all checkbox
    watch(someSelected, (value) => {
      if (selectAllCheckbox.value) {
        selectAllCheckbox.value.indeterminate = value
      }
    })

    return {
      searchQuery,
      sortKey,
      sortDir,
      currentPage,
      internalPageSize,
      selectedItems,
      viewMode,
      selectAllCheckbox,
      visibleColumns,
      filteredItems,
      paginatedItems,
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      visiblePages,
      allSelected,
      someSelected,
      getNestedValue,
      getItemKey,
      formatValue,
      handleSearch,
      clearSearch,
      handleSort,
      goToPage,
      handlePageSizeChange,
      handleRowClick,
      isSelected,
      toggleSelect,
      toggleSelectAll,
      clearSelection
    }
  }
}
</script>
